import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Download, Camera, LogIn, Eye, Pencil, ShieldAlert, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { copyText } from './consoleUtils';

const TYPES = [['', 'All activity'], ['action', 'Changes'], ['page_view', 'Page views'], ['screenshot', 'Screenshots'], ['login', 'Sign-ins'], ['login_failed', 'Failed sign-ins']];
const TYPE_ICON = { action: Pencil, page_view: Eye, screenshot: Camera, login: LogIn, login_failed: ShieldAlert };
const PAGE = 50;
const selectCls = 'flex h-9 rounded-md border border-input bg-background px-3 text-sm';
const fmt = (iso) => new Date(iso).toLocaleString();
const rowText = (r) => [fmt(r.created_at), r.user_name, r.user_email, r.type, r.action, r.module, r.detail, r.ip].join('\t');

export const AuditTab = ({ accounts }) => {
  const [filters, setFilters] = useState({ user_id: '', type: '', q: '', date_from: '', date_to: '', hide_console: true });
  const [data, setData] = useState({ items: [], total: 0 });
  const [skip, setSkip] = useState(0);
  const set = (k) => (e) => { setSkip(0); setFilters({ ...filters, [k]: e.target.value }); };

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/console/audit', { params: { ...filters, skip, limit: PAGE } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load activity'); }
  }, [filters, skip]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportExcel = async () => {
    try {
      const r = await axios.get('/api/console/audit/export', { params: filters, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `portal_audit_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const copyAll = () => copyText(['Date & Time\tUser\tEmail\tType\tAction\tModule\tDetails\tIP', ...data.items.map(rowText)].join('\n'), `${data.items.length} rows copied`);

  return (
    <Card className="border-border/40" data-testid="console-audit-tab">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          <select className={`${selectCls} w-full sm:w-52`} value={filters.user_id} onChange={set('user_id')} data-testid="audit-filter-user">
            <option value="">All accounts</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
          </select>
          <select className={`${selectCls} w-full sm:w-44`} value={filters.type} onChange={set('type')} data-testid="audit-filter-type">
            {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Input className="w-full sm:w-52" placeholder="Search details" value={filters.q} onChange={set('q')} data-testid="audit-filter-search" />
          <Input type="date" className="w-[48%] sm:w-40" value={filters.date_from} onChange={set('date_from')} data-testid="audit-filter-from" />
          <Input type="date" className="w-[48%] sm:w-40" value={filters.date_to} onChange={set('date_to')} data-testid="audit-filter-to" />
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={filters.hide_console} onChange={e => { setSkip(0); setFilters({ ...filters, hide_console: e.target.checked }); }} data-testid="audit-hide-console" />
            Hide Admin Console activity
          </label>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground" data-testid="audit-total">{data.total} event(s)</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="btn-audit-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
            <Button variant="outline" size="sm" onClick={copyAll} data-testid="btn-audit-copy"><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
            <Button size="sm" onClick={exportExcel} data-testid="btn-audit-export"><Download className="w-3.5 h-3.5 mr-1" /> Export Excel</Button>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">Screenshots are logged when the Print Screen key or a common PC screenshot shortcut is pressed. Phone screenshots can't be detected by websites.</p>
        <div className="divide-y divide-border/40 border border-border/40 rounded-md">
          {data.items.map(r => {
            const Icon = TYPE_ICON[r.type] || Pencil;
            const alert = r.type === 'screenshot' || r.type === 'login_failed';
            return (
              <div key={r.id} className={`p-3 flex items-start gap-3 text-xs ${alert ? 'bg-amber-50 dark:bg-amber-950/30' : ''}`} data-testid={`audit-row-${r.id}`}>
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${alert ? 'text-amber-600' : 'text-muted-foreground'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-x-2 items-baseline">
                    <span className="font-semibold text-foreground">{r.user_name || 'Unknown'}</span>
                    <span className="text-muted-foreground">{r.action}{r.module ? ` · ${r.module}` : ''}</span>
                  </div>
                  <div className="text-muted-foreground break-all">{r.detail}</div>
                  <div className="text-[10px] text-muted-foreground/80 mt-0.5">{fmt(r.created_at)}{r.ip ? ` · ${r.ip}` : ''}</div>
                </div>
                <button title="Copy" className="p-1 text-muted-foreground hover:text-primary" onClick={() => copyText(rowText(r))} data-testid={`btn-copy-audit-${r.id}`}>
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
          {data.items.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No activity found</p>}
        </div>
        <div className="flex justify-end items-center gap-2">
          <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))} data-testid="btn-audit-prev">Previous</Button>
          <span className="text-xs text-muted-foreground">{data.total ? skip + 1 : 0}–{Math.min(skip + PAGE, data.total)}</span>
          <Button variant="outline" size="sm" disabled={skip + PAGE >= data.total} onClick={() => setSkip(skip + PAGE)} data-testid="btn-audit-next">Next</Button>
        </div>
      </CardContent>
    </Card>
  );
};
